import { supabase } from "@/lib/supabase/browser-client"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import {
  SpaceMemberDirectory,
  SpaceMemberInsert,
  SpaceMemberUpdate,
  SpaceMemberWithProfile,
  SpaceRole
} from "@/types/spaces"

export type SpaceMemberItemScope = {
  itemId?: string | null
  itemType?: "note" | "task" | null
}

export type SpaceMemberQueryOptions = SpaceMemberItemScope & {
  query?: string
  limit?: number
}

const buildMembersQuery = (
  spaceId: string,
  options: SpaceMemberQueryOptions = {},
  includeWorkspaceCandidates = false
) => {
  const params = new URLSearchParams()

  if (options.query?.trim()) {
    params.set("q", options.query.trim())
  }

  if (typeof options.limit === "number") {
    params.set("limit", String(options.limit))
  }

  if (options.itemType && options.itemId) {
    params.set("itemType", options.itemType)
    params.set("itemId", options.itemId)
  }

  if (includeWorkspaceCandidates) {
    params.set("includeWorkspaceCandidates", "true")
  }

  const query = params.toString()
  return `/api/spaces/${spaceId}/members${query ? `?${query}` : ""}`
}

const getResponseError = async (response: Response, fallback: string) => {
  const payload = (await response.clone().json().catch(() => null)) as
    | { error?: string; details?: { validationErrors?: string[] } | string[] }
    | string
    | null

  if (typeof payload === "string" && payload.trim()) {
    return payload
  }

  if (payload && typeof payload === "object" && "error" in payload) {
    const validationErrors = Array.isArray(payload.details)
      ? payload.details
      : Array.isArray(payload.details?.validationErrors)
      ? payload.details.validationErrors
      : []

    return [payload.error || fallback, ...validationErrors]
      .filter(Boolean)
      .join(" ")
  }

  const text = await response.text().catch(() => "")
  if (text.trim()) {
    return text
  }

  return fallback
}

export const getSpaceMembers = async (
  spaceId: string,
  options: SpaceMemberQueryOptions = {}
) => {
  const response = await authenticatedFetch(buildMembersQuery(spaceId, options), {
    cache: "no-store"
  })

  if (!response.ok) {
    throw new Error(
      await getResponseError(response, "Failed to load space members")
    )
  }

  return (await response.json()) as SpaceMemberWithProfile[]
}

export const getSpaceMemberDirectory = async (
  spaceId: string,
  options: SpaceMemberQueryOptions = {}
) => {
  const response = await authenticatedFetch(buildMembersQuery(spaceId, options, true), {
    cache: "no-store"
  })

  if (!response.ok) {
    throw new Error(
      await getResponseError(response, "Failed to load space access")
    )
  }

  const payload = (await response.json()) as SpaceMemberDirectory

  return {
    ...payload,
    members: payload.members || [],
    workspaceCandidates: payload.workspaceCandidates || []
  } satisfies SpaceMemberDirectory
}

export const getSpaceMember = async (spaceId: string, userId: string) => {
  const { data, error } = await supabase
    .from("space_members")
    .select("*")
    .eq("space_id", spaceId)
    .eq("user_id", userId)
    .single()

  if (error) {
    if (error.code === "PGRST116") {
      // Not found
      return null
    }
    throw error
  }

  return data
}

export const getUserSpaces = async (userId: string) => {
  const { data, error } = await supabase
    .from("space_members")
    .select(
      `
      *,
      space:spaces(*)
    `
    )
    .eq("user_id", userId)
    .order("last_viewed_at", { ascending: false, nullsFirst: false })

  if (error) throw error
  return data
}

export const addSpaceMember = async (
  spaceId: string, 
  email: string, 
  role: string = "viewer"
) => {
  // First, find the user by email
  const { data: profileData, error: profileError } = await supabase
    .from("profiles")
    .select("user_id")
    .eq("email", email)
    .single()

  if (profileError || !profileData) {
    throw new Error("User not found with that email address")
  }

  // Get current user for added_by field
  const { data: { user } } = await supabase.auth.getUser()
  
  const { data, error } = await supabase
    .from("space_members")
    .insert({
      space_id: spaceId,
      user_id: profileData.user_id,
      role,
      added_by: user?.id
    })
    .select()
    .single()

  if (error) {
    if (error.code === '23505') { // Unique constraint violation
      throw new Error("User is already a member of this space")
    }
    throw error
  }
  
  return data
}

export const updateSpaceMemberRole = async (
  spaceId: string,
  userId: string,
  role: SpaceRole
) => {
  const { data, error } = await supabase
    .from("space_members")
    .update({ role })
    .eq("space_id", spaceId)
    .eq("user_id", userId)
    .select()
    .single()

  if (error) throw error
  return data
}

export const removeSpaceMember = async (spaceId: string, userId: string) => {
  const { error } = await supabase
    .from("space_members")
    .delete()
    .eq("space_id", spaceId)
    .eq("user_id", userId)

  if (error) throw error
}

export const updateMemberLastViewed = async (spaceId: string, userId: string) => {
  const { error } = await supabase
    .from("space_members")
    .update({ last_viewed_at: new Date().toISOString() })
    .eq("space_id", spaceId)
    .eq("user_id", userId)

  if (error) throw error
}

export const isSpaceMember = async (
  spaceId: string,
  userId: string
): Promise<boolean> => {
  const { data, error } = await supabase
    .from("space_members")
    .select("user_id")
    .eq("space_id", spaceId)
    .eq("user_id", userId)
    .single()

  if (error && error.code !== "PGRST116") throw error

  return !!data
}

export const getSpaceOwners = async (spaceId: string) => {
  const { data, error } = await supabase
    .from("space_members")
    .select(
      `
      *,
      user:profiles!space_members_user_profile_fkey(id, username, image_url)
    `
    )
    .eq("space_id", spaceId)
    .eq("role", "owner")

  if (error) {
    throw error
  }

  return data
}
